/**
 * Full-screen song page (/songs/:songId, outside the AppShell): top bar, Nótur (sheet viewer with
 * annotation layers; "Skrifa" switches to edit mode) and Texti (lyrics) tabs, and the
 * practice-audio dock. The AudioProvider sits above the tabs so switching tabs or modes never
 * interrupts playback (switching SONGS — a song tab, Fyrra / Næsta — does stop it). While a pack
 * downloads, its progress floats over the top-left corner of the sheet (display only: it never
 * resizes the sheet and never takes a tap; hidden in edit mode), or sits under the top bar on the
 * lyrics / audio-only views.
 *
 * ?c=<collectionId>&i=<index>&e=<entryId> = setlist context: "‹ Fyrra · 3 / 12 · Næsta ›" (history
 * replace, so Back returns to the setlist; "3 / 12" opens the list drawer), the next song prefetched
 * (details + 's' bundle), and a "Næsta lag ›" chip after "next" on the last page. A second key /
 * pedal press opens the next song once the chip has shown for NEXT_SONG_ARM_MS (viewer/setlist;
 * never an auto-repeated keydown; taps and swipes only show the chip — tap the chip itself, which is
 * armed the same way, so the second tap of a double tap on the last page never skips a song). Each
 * setlist entry is its own page instance (keyed by song and entry), so a song listed twice in a row
 * starts again at page 1.
 *
 * Song tabs ("Opin lög", tabs/): the strip floats over the sheet (also on the loading / not-found
 * states, so a broken song can be left) — the sheet keeps the whole height under the top bar; the
 * chips on the sheet keep clear of it (CSS --tabs-top / --tabs-bottom). It holds the sheet choice
 * ("Velja nótur") and, for a list's tab, the list drawer. A strip switch restores the tab's sheet
 * and page (useTabSync).
 *
 * Page layout ("Útlit síðu": Samfellt | Heil síða | Tvær síður) is a device preference
 * (viewer/pageMode); the top bar offers "Tvær síður" while the viewer reports it wide enough.
 *
 * Edit mode ("Skrifa") is full screen: the top bar and the tab strip step aside and the sheet keeps
 * the whole area; the floating toolbar (annotate/Toolbar) has "Lokið" and the page buttons.
 *
 * Reading mode (viewer/readingMode): the top bar, the setlist row, the strip and the player fold into
 * one floating button (ReadingButton) — automatic on short screens (a phone in landscape), else with
 * "Fela stikur". A tap on the button shows them again OVER the sheet (it never shrinks; playback
 * never stops): "Festa stikur" keeps them, ⌃, Escape or a tap on the sheet folds them again; a new
 * song starts folded.
 */
import { evictRefusedFile, syncManifest } from '../offline/manager';
import { useOfflineSong } from '../offline/songFallback';
import { SaveOfflineButton } from '../offline/SaveOfflineButton';
import { DownloadsBar } from '../offline/DownloadsBar';
import { useQueryClient } from '@tanstack/react-query';
import { FileMusic, Music, SearchX, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps, type CSSProperties, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { roleAtLeast } from '../../shared/audience';
import type { SongDTO, SongListItemDTO } from '../../shared/dto/library';
import type { MeDTO } from '../../shared/dto/auth';
import { ApiFailure, NetworkFailure } from '../api/client';
import { SONGS_KEY } from '../api/library';
import { isQueryGone, isQueryKeyGone } from '../api/queryClient';
import { AudioFullControls } from '../audio/AudioControls';
import { AudioDock } from '../audio/AudioDock';
import { AudioProvider } from '../audio/AudioProvider';
import { useRequiredMe } from '../auth/AuthProvider';
import { useToast } from '../components/toast';
import { Button, EmptyState } from '../components/ui';
import { useT } from '../i18n';
import { SheetWorkspace } from '../annotate/SheetWorkspace';
import { filesForViewer, hasHiddenFiles, isPlayableAudio, isViewableSheet, pickDefaultFile } from '../viewer/fileChoice';
import { LyricsView } from '../viewer/LyricsView';
import { SheetViewerEnvContext, type SheetViewerEnv } from '../viewer/env';
import { NextSongChip } from '../viewer/SetlistNav';
import { nextChipArmed, nextPastEndAction, parseSetlist, pastEndResult, type SetlistContext } from '../viewer/setlist';
import { twoUpEligible as twoUpEligibleBySize } from '../viewer/spreads';
import { SongTopBar, type SongTab } from '../viewer/SongTopBar';
import { CollectionDrawer } from '../tabs/CollectionDrawer';
import type { SheetChoice } from '../tabs/SheetMenu';
import { TabStrip } from '../tabs/TabStrip';
import { useTabSync, type TabSync } from '../tabs/useTabSync';
import { PREF, readPref, writePref } from '../viewer/storage';
import { usePrefetchSong, useSetlistNav, type SetlistNavigation } from '../viewer/useSetlist';
import { isSongGone, songKey, useSong } from '../viewer/useSong';
import { useWakeLock } from '../viewer/useWakeLock';
import { PracticeButton } from '../practice/PracticeMenu';
import { NO_INSETS, type DockInsets } from '../lib/useFloatingDock';
import { barsFolded, readBarsPref, useShortScreen, writeBarsPref, type BarsPref } from '../viewer/readingMode';
import { ReadingButton } from '../viewer/ReadingButton';

type SongTopBarProps = ComponentProps<typeof SongTopBar>;

const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isStr = (v: unknown): v is string | null => v === null || typeof v === 'string';

function useGoBack(setlist: SetlistContext | null) {
  const navigate = useNavigate();
  return useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(setlist ? `/collections/${setlist.collectionId}` : '/songs', { replace: true });
  }, [navigate, setlist]);
}

/**
 * The server said the song is gone (403/404: deleted, or no longer visible to me). TanStack keeps the
 * old (persisted) data next to that error: it is never shown, not persisted again (shouldPersistQuery),
 * and every other copy goes now instead of at the next manifest sync — the pack / recent copies of its
 * files (a 403 eviction drops every revision and re-syncs the manifest) and, through that sync, the
 * song details and lyrics (members; editors keep song details, so the stale copy is dropped from
 * memory when the page closes). Sticky: a later refetch that fails offline or with a 5xx replaces
 * the 404 as the error but keeps the data (isQueryGone), and while the page is open the sync may
 * remove the query this page observes — its refetch must not flash the loading skeleton before the 404.
 */
function useGoneSong(songId: string | undefined, q: ReturnType<typeof useSong>): boolean {
  const qc = useQueryClient();
  const [goneId, setGoneId] = useState<string | null>(null);
  const goneNow = !!songId && (isSongGone(q.error) || isQueryKeyGone(qc, songKey(songId)));
  if (goneNow && goneId !== songId) setGoneId(songId);
  const gone = goneNow || (!!songId && goneId === songId && q.data === undefined);
  const stale = useRef(q.data);
  stale.current = q.data;
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!gone || !songId || handled.current === songId) return;
    handled.current = songId;
    for (const f of stale.current?.id === songId ? stale.current.files : []) void evictRefusedFile(f.id, 403, f.rev);
    void syncManifest();
  }, [gone, songId]);
  // When the page closes (or moves on to the next song) — not while the page observes it: a removed
  // query is refetched by the next render. Kept only when fresh data arrived since the 404.
  useEffect(() => {
    if (!songId || goneId !== songId) return;
    return () => {
      const query = qc.getQueryCache().find({ queryKey: songKey(songId), exact: true });
      if (query && isQueryGone(query)) qc.removeQueries({ queryKey: songKey(songId), exact: true });
    };
  }, [goneId, songId, qc]);
  return gone;
}

export default function SongScreen() {
  const { songId } = useParams();
  const [params] = useSearchParams();
  // Follows the URL: setlist prev/next replaces it with the next song and index.
  const setlist = useMemo(() => parseSetlist(params), [params]);
  const me = useRequiredMe();
  const q = useSong(songId);
  const gone = useGoneSong(songId, q);
  const song = gone ? undefined : q.data;
  // No connection (failed, retrying, or paused because the device went offline — offlineFirst) and
  // the song details were never cached: rebuild them from the offline pack manifest.
  const netDown = !song && !gone && (q.fetchStatus === 'paused' || q.failureReason instanceof NetworkFailure || q.error instanceof NetworkFailure);
  const fallback = useOfflineSong(songId, netDown);
  const goBack = useGoBack(setlist);
  const nav = useSetlistNav(setlist, songId);
  usePrefetchSong(nav?.next?.songId ?? null, me);
  useWakeLock(true);
  const tabs = useTabSync({ userId: me.id, songId, setlist, found: !!(song ?? fallback), gone });

  // One instance per setlist entry: a song listed twice in a row restarts at page 1.
  const entry = setlist ? `:${setlist.collectionId}:${setlist.entryId ?? setlist.index}` : '';
  const common = { onBack: goBack, nav, tabs };
  if (!songId) return <SongMessage kind="not_found" {...common} />;
  if (song) return <SongBody key={song.id + entry} song={song} me={me} {...common} />;
  if (fallback) return <SongBody key={fallback.id + entry} song={fallback} me={me} {...common} />;
  if (gone) return <SongMessage kind="not_found" {...common} />;
  // Paused offline (the device went offline during the session): it resumes by itself when the
  // connection returns, so no retry button.
  if (q.isPending && q.fetchStatus === 'paused') return <SongMessage kind="offline" {...common} />;
  if (q.isError) {
    const e = q.error;
    const kind =
      e instanceof ApiFailure && (e.status === 404 || e.status === 403)
        ? 'not_found'
        : e instanceof NetworkFailure
          ? 'offline'
          : 'error';
    return <SongMessage kind={kind} onRetry={() => void q.refetch()} {...common} />;
  }
  return <SongSkeleton {...common} />;
}

/* ─── Loading / error states ────────────────────────────────────────────── */

function SongSkeleton({ onBack, tabs }: { onBack: () => void; nav: SetlistNavigation | null; tabs: TabSync }) {
  const t = useT();
  return (
    <div className="flex h-dvh flex-col bg-[var(--bg)]">
      <SongTopBar
        title={t('common.loading')}
        onBack={onBack}
        tab="sheet"
        onTab={() => undefined}
        showTabs={false}
        allFiles={false}
        onAllFiles={() => undefined}
        showAllFilesToggle={false}
        dockOpen={false}
        onDock={() => undefined}
        showDockToggle={false}
      />
      <div className="relative min-h-0 flex-1">
        <div className="h-full overflow-hidden px-1.5 pt-2" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="mx-auto mb-2.5 aspect-[1/1.414] w-full max-w-[1400px] animate-pulse bg-[var(--surface)] shadow-sm" />
          ))}
        </div>
        <FloatLayer>
          <TabStrip sync={tabs} onBack={onBack} />
        </FloatLayer>
      </div>
    </div>
  );
}

/** Not found / not saved / failed — the setlist controls and the tabs stay so the singer can move on. */
function SongMessage({
  kind,
  onBack,
  onRetry,
  nav,
  tabs,
}: {
  kind: 'not_found' | 'offline' | 'error';
  onBack: () => void;
  onRetry?: () => void;
  nav: SetlistNavigation | null;
  tabs: TabSync;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [listOpen, setListOpen] = useState(false);
  return (
    <div className="flex h-dvh flex-col bg-[var(--bg)]">
      <SongTopBar
        title=""
        onBack={onBack}
        tab="sheet"
        onTab={() => undefined}
        showTabs={false}
        allFiles={false}
        onAllFiles={() => undefined}
        showAllFilesToggle={false}
        dockOpen={false}
        onDock={() => undefined}
        showDockToggle={false}
        setlist={nav}
        onShowList={tabs.list ? () => setListOpen(true) : undefined}
      />
      <CollectionDrawer open={listOpen} onClose={() => setListOpen(false)} list={tabs.list} />
      <DownloadsBar />
      <div className="relative flex min-h-0 flex-1 items-center justify-center" style={{ paddingBottom: 'var(--safe-bottom)' }}>
        <FloatLayer>
          <TabStrip message sync={tabs} onBack={onBack} onOpenList={() => setListOpen(true)} />
        </FloatLayer>
        {kind === 'not_found' ? (
          <EmptyState icon={SearchX} title={t('viewer.song_not_found')} action={<Button onClick={() => navigate('/songs')}>{t('viewer.to_songs')}</Button>}>
            {t('viewer.song_not_found_hint')}
          </EmptyState>
        ) : kind === 'offline' ? (
          <EmptyState icon={WifiOff} title={t('viewer.song_offline')} action={onRetry ? <Button onClick={onRetry}>{t('common.retry')}</Button> : undefined}>
            {t('viewer.song_offline_hint')}
          </EmptyState>
        ) : (
          <EmptyState icon={Music} title={t('viewer.song_error')} action={onRetry ? <Button onClick={onRetry}>{t('common.retry')}</Button> : undefined}>
            {t('common.error')}
          </EmptyState>
        )}
      </div>
    </div>
  );
}

/** Where a floating bar (the strip of open songs, reading mode's button) floats: over the area below the top bar, inside the safe area. */
function FloatLayer({ children, top = 0, bottom = 'var(--safe-bottom)' }: { children: ReactNode; top?: string | number; bottom?: string | number }) {
  return (
    <div className="pointer-events-none absolute z-30" style={{ top, left: 'var(--safe-left)', right: 'var(--safe-right)', bottom }}>
      {children}
    </div>
  );
}

/** Height of an element, following resizes (a callback ref and the height). */
function useElementHeight(): [(el: HTMLElement | null) => void, number] {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    if (!el) return;
    const measure = () => setHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, height];
}

/* ─── The song page ─────────────────────────────────────────────────────── */

/**
 * Selected file id of a list: keeps the user's pick while it exists, else the default rule.
 * `initialId` (a song tab's remembered sheet, on a strip switch) starts as the pick.
 */
function useFileChoice(list: SongDTO['files'], me: MeDTO, lastKey: string, initialId: string | null = null) {
  const [picked, setPicked] = useState<string | null>(initialId);
  const selected = useMemo(() => {
    if (picked && list.some((f) => f.id === picked)) return list.find((f) => f.id === picked)!;
    return pickDefaultFile(list, me.section, readPref<string | null>(lastKey, null, isStr));
  }, [list, picked, me.section, lastKey]);
  const select = useCallback(
    (id: string) => {
      setPicked(id);
      writePref(lastKey, id);
    },
    [lastKey],
  );
  return [selected, select] as const;
}

function SongBody({ song, me, onBack, nav, tabs }: { song: SongDTO; me: MeDTO; onBack: () => void; nav: SetlistNavigation | null; tabs: TabSync }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const isEditor = roleAtLeast(me.role, 'editor');
  const [allFiles, setAllFilesState] = useState(() => isEditor && readPref(PREF.allFiles, false, isBool));
  const setAllFiles = (v: boolean) => {
    setAllFilesState(v);
    writePref(PREF.allFiles, v || null);
  };

  // Unready files (still uploading) are dropped BEFORE the editor's own-audience choice.
  const sheets = useMemo(() => filesForViewer(song.files, 'sheet', me, allFiles, isViewableSheet), [song.files, me, allFiles]);
  const lyrics = useMemo(() => filesForViewer(song.files, 'lyrics', me, allFiles), [song.files, me, allFiles]);
  const tracks = useMemo(() => filesForViewer(song.files, 'audio', me, allFiles, isPlayableAudio), [song.files, me, allFiles]);
  const [sheet, selectSheet] = useFileChoice(sheets, me, PREF.lastSheet(song.id), tabs.initialSheetId);
  const [lyricsFile, selectLyrics] = useFileChoice(lyrics, me, PREF.lastLyrics(song.id));

  const hasSheet = sheets.length > 0;
  const hasLyrics = lyrics.length > 0;
  const hasAudio = tracks.length > 0;
  const [tabState, setTabState] = useState<SongTab>(hasSheet ? 'sheet' : 'lyrics');
  const tab: SongTab = tabState === 'sheet' && !hasSheet ? 'lyrics' : tabState === 'lyrics' && !hasLyrics ? 'sheet' : tabState;
  // Edit mode ("Skrifa") exists only on the Nótur tab; leaving the tab ends it.
  const [editingState, setEditing] = useState(false);
  const editing = editingState && tab === 'sheet' && !!sheet;
  const setTab = (next: SongTab) => {
    setTabState(next);
    if (next !== 'sheet') setEditing(false);
  };
  // Whether the viewer is wide enough for "Tvær síður" (the layout itself is a device preference,
  // viewer/pageMode). First guess from the window (no option popping in after the viewer measures);
  // the viewer corrects it.
  const [twoUpEligible, setTwoUpEligible] = useState(() => typeof window !== 'undefined' && twoUpEligibleBySize(window.innerWidth, window.innerHeight));

  // "Næsta lag ›": shown after "next" on the last page; a second key / pedal "next" while it shows
  // opens the next song (pedal-only singers) — see nextPastEndAction for the guards against double
  // presses, held keys and taps. Hidden again when the last page leaves the view.
  const [pastEnd, setPastEnd] = useState(false);
  const pastEndAt = useRef(0);
  const live = useRef({ nav, editing, pastEnd, tabs });
  live.current = { nav, editing, pastEnd, tabs };
  const songId = song.id;
  const viewerEnv = useMemo<SheetViewerEnv>(
    () => ({
      onTwoUpEligible: setTwoUpEligible,
      onNextPastEnd: (ev) => {
        const { nav: n, editing: e, pastEnd: shown } = live.current;
        const now = performance.now();
        const action = nextPastEndAction({ hasNext: !!n?.next, editing: e, chipShownAt: shown ? pastEndAt.current : null, now }, ev);
        if (action === 'show') {
          pastEndAt.current = now;
          setPastEnd(true);
        } else if (action === 'open' && n?.next) n.go(n.next);
        // A next song follows (the chip is up — a tap / quick press while it shows): handled, so the
        // viewer never adds "Síðasta síða" beside the chip. 'none' only when nothing follows.
        return pastEndResult(action, !!n?.next, e);
      },
      onAtEnd: (atEnd) => {
        if (!atEnd) setPastEnd(false);
      },
      // Song tabs: the page a strip switch returns to, and every page read (through the ref: the
      // value stays stable, so the viewer never re-renders for it).
      initialPage: (fileId) => live.current.tabs.initialPage(fileId),
      onPage: (fileId, rev, page) => live.current.tabs.onPage(songId, fileId, rev, page),
    }),
    [songId],
  );
  const nextTitle = nav?.next
    ? (qc.getQueryData<SongDTO>(songKey(nav.next.songId))?.title ??
      qc.getQueryData<SongListItemDTO[]>(SONGS_KEY)?.find((x) => x.id === nav.next!.songId)?.title ??
      null)
    : null;

  const [dockOpen, setDockOpenState] = useState(() => readPref(PREF.dock, true, isBool));
  const setDockOpen = (v: boolean) => {
    setDockOpenState(v);
    writePref(PREF.dock, v ? null : false);
  };

  // A newer revision of the open sheet arrived (refetch after a 404 or a background refresh).
  const lastRev = useRef<{ id: string; rev: number } | null>(null);
  useEffect(() => {
    if (!sheet) return;
    const prev = lastRev.current;
    if (prev && prev.id === sheet.id && sheet.rev > prev.rev) toast.show(t('viewer.rev_loaded'), { tone: 'ok' });
    lastRev.current = { id: sheet.id, rev: sheet.rev };
  }, [sheet, toast, t]);

  const onStaleRevision = useCallback(() => {
    void qc.invalidateQueries({ queryKey: songKey(song.id) });
  }, [qc, song.id]);

  const audioOnly = !hasSheet && !hasLyrics && hasAudio;
  const nothing = !hasSheet && !hasLyrics && !hasAudio;
  const showDock = hasAudio && !audioOnly && dockOpen;
  // Pack download progress: over the sheet's corner while the sheet shows (never resizes it, never
  // takes a tap); in the flow under the top bar on the other views; hidden in edit mode.
  const sheetShown = tab === 'sheet' && !!sheet;
  const subtitle = [song.composer, song.arranger].filter(Boolean).join(' · ') || null;

  // The sheet shown goes into the song's tab (its second line; a strip switch brings it back).
  const sheetId = sheet?.id ?? null;
  useEffect(() => {
    if (sheetId) tabs.onSheetShown(songId, sheetId);
  }, [tabs, songId, sheetId]);

  // The sheet choice lives in the tab strip ("Velja nótur") and the list drawer — offered on the
  // Nótur tab only (on Texti the tab just names the sheet).
  const sheetChoice = useMemo<SheetChoice | null>(
    () => (sheets.length > 0 ? { files: sheets, selectedId: sheetId, onSelect: selectSheet } : null),
    [sheets, sheetId, selectSheet],
  );
  const [listOpen, setListOpen] = useState(false);
  const openList = tabs.list && !editing ? () => setListOpen(true) : undefined;
  // Reading mode: the bars folded into one floating button, or shown over the sheet.
  const shortScreen = useShortScreen();
  const [barsPref, setBarsPrefState] = useState<BarsPref>(readBarsPref);
  const setBarsPref = (p: BarsPref) => {
    setBarsPrefState(p);
    writeBarsPref(p);
  };
  const folded = !editing && barsFolded(barsPref, shortScreen);
  const [barsOverState, setBarsOver] = useState(false);
  const barsOver = folded && barsOverState;
  const [overRef, overHeight] = useElementHeight();
  const [dockOverRef, dockOverHeight] = useElementHeight();
  // The player sits under the sheet, except in reading mode: then it comes with the bars, over the sheet.
  const dockInFlow = showDock && !folded;
  const floatBottom = dockInFlow ? 0 : barsOver && showDock ? dockOverHeight : 'var(--safe-bottom)';
  useEffect(() => {
    if (!barsOver) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[aria-modal="true"]')) setBarsOver(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [barsOver]);
  // What the floating strip / button covers (nothing while writing: they step aside).
  const [stripInsets, setStripInsets] = useState<DockInsets>(NO_INSETS);
  const [buttonInsets, setButtonInsets] = useState<DockInsets>(NO_INSETS);
  const covered = editing ? NO_INSETS : folded && !barsOver ? buttonInsets : stripInsets;
  const topBar = (reading: SongTopBarProps['reading']) => (
    <SongTopBar
      title={song.title}
      subtitle={subtitle}
      onBack={onBack}
      tab={tab}
      onTab={setTab}
      showTabs={hasSheet && hasLyrics}
      allFiles={allFiles}
      onAllFiles={setAllFiles}
      showAllFilesToggle={isEditor && (allFiles || hasHiddenFiles(song.files, me))}
      dockOpen={dockOpen}
      onDock={setDockOpen}
      showDockToggle={hasAudio && !audioOnly}
      edit={tab === 'sheet' && sheet ? { editing, onToggle: () => setEditing(true) } : null}
      extra={<SaveOfflineButton songId={song.id} />}
      pageMode={tab === 'sheet' && sheet ? { twoUpEligible } : null}
      setlist={nav}
      onShowList={openList}
      practice={<PracticeButton current={tabs.list} />}
      reading={reading}
    />
  );

  return (
    <AudioProvider key={song.id} songId={song.id} songTitle={song.title} tracks={tracks} onSourceError={onStaleRevision}>
      <div className="relative flex h-dvh flex-col overflow-hidden bg-[var(--bg)]">
        {editing || folded
          ? null
          : topBar({
              shownOver: false,
              onFold: () => {
                setBarsPref('folded');
                setBarsOver(false);
              },
            })}
        {barsOver ? (
          // Over the sheet, which keeps its size.
          <div ref={overRef} className="absolute inset-x-0 top-0 z-40 shadow-[0_8px_30px_rgb(0_0_0/0.28)]">
            {topBar({
              shownOver: true,
              onPin: () => {
                setBarsPref('pinned');
                setBarsOver(false);
              },
              onClose: () => setBarsOver(false),
            })}
          </div>
        ) : null}
        <CollectionDrawer open={listOpen && !editing} onClose={() => setListOpen(false)} list={tabs.list} sheets={tab === 'sheet' ? sheetChoice : null} />
        {sheetShown ? null : <DownloadsBar />}
        <main
          className="relative min-h-0 flex-1"
          style={
            {
              paddingTop: editing || folded ? 'var(--top-inset)' : undefined,
              paddingBottom: dockInFlow ? undefined : 'var(--safe-bottom)',
              paddingLeft: 'var(--safe-left)',
              paddingRight: 'var(--safe-right)',
              '--tabs-top': `${covered.top}px`,
              '--tabs-bottom': `${covered.bottom}px`,
            } as CSSProperties
          }
        >
          {nothing ? (
            <div className="flex h-full items-center justify-center">
              <EmptyState icon={FileMusic} title={t('viewer.no_files')}>
                {t('viewer.no_files_hint')}
              </EmptyState>
            </div>
          ) : audioOnly ? (
            <div className="scroll-y h-full">
              <div className="mx-auto max-w-2xl px-4 py-6">
                <EmptyState icon={FileMusic} title={t('viewer.no_sheet')}>
                  {t('viewer.no_sheet_audio_hint')}
                </EmptyState>
                <div className="rounded-[var(--radius-card)] border border-[var(--line)] bg-[var(--surface)] p-5 shadow-[var(--shadow-card)]">
                  <AudioFullControls />
                </div>
              </div>
            </div>
          ) : (
            <div className="relative h-full">
              {sheet ? (
                <div
                  className="absolute inset-0"
                  style={tab === 'sheet' ? undefined : { visibility: 'hidden', pointerEvents: 'none' }}
                  aria-hidden={tab !== 'sheet'}
                >
                  <SheetViewerEnvContext.Provider value={viewerEnv}>
                    <SheetWorkspace file={sheet} active={tab === 'sheet'} editing={editing} onStaleRevision={onStaleRevision} onDone={() => setEditing(false)} />
                  </SheetViewerEnvContext.Provider>
                </div>
              ) : null}
              {pastEnd && nav?.next && !editing && tab === 'sheet' ? (
                <NextSongChip
                  title={nextTitle}
                  // The second tap of a double tap on the last page lands here: armed like the pedal (D7).
                  onOpen={() => {
                    if (nextChipArmed(pastEndAt.current, performance.now())) nav.go(nav.next!);
                  }}
                />
              ) : null}
              {hasLyrics && tab === 'lyrics' ? (
                <div className="absolute inset-0">
                  <LyricsView files={lyrics} selectedId={lyricsFile?.id ?? null} onSelect={selectLyrics} />
                </div>
              ) : null}
            </div>
          )}
          {sheetShown && !editing ? (
            // Floats over the sheet's top-left corner (the page indicator is top-right), below the
            // strip when it lies along the top, so the sheet (a two-up spread) never rescales;
            // display only (no stray trip to Settings).
            <div
              className="pointer-events-none absolute z-20 max-w-[min(30rem,calc(100%-9rem))] overflow-hidden rounded-xl shadow-md ring-1 ring-[var(--line)] empty:hidden"
              style={{ left: 'calc(var(--safe-left) + 0.5rem)', top: 'calc(var(--tabs-top) + 0.5rem)' }}
            >
              <DownloadsBar />
            </div>
          ) : null}
          {barsOver ? (
            // A tap on the sheet folds the bars again (and turns no page).
            <div className="absolute inset-0 z-[25]" onClick={() => setBarsOver(false)} aria-hidden />
          ) : null}
          {folded && !barsOver ? (
            <FloatLayer top="var(--top-inset)" bottom={floatBottom}>
              <ReadingButton onOpen={() => setBarsOver(true)} onInsets={setButtonInsets} />
            </FloatLayer>
          ) : null}
          {editing || (folded && !barsOver) ? null : (
            <FloatLayer top={barsOver ? overHeight : 0} bottom={floatBottom}>
              <TabStrip sync={tabs} sheets={sheetChoice} sheetMenu={tab === 'sheet'} onOpenList={openList} onBack={onBack} onInsets={setStripInsets} />
            </FloatLayer>
          )}
        </main>
        {dockInFlow ? <AudioDock /> : null}
        {barsOver && showDock ? (
          <div ref={dockOverRef} className="absolute inset-x-0 bottom-0 z-40 shadow-[0_-8px_30px_rgb(0_0_0/0.28)]">
            <AudioDock />
          </div>
        ) : null}
      </div>
    </AudioProvider>
  );
}
